import React, { memo, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import {
    TechnicalActionCounts,
    TechnicalRatingLabel,
    isBearishLabel,
    isBullishLabel
} from '../../util/technicals'

const GAUGE_ANIMATION_MS = 480

/** Ease-out cubic lerp of score (−1…+1) when timeframe / data changes. */
const useAnimatedScore = (target: number, durationMs = GAUGE_ANIMATION_MS): number => {
    const [value, setValue] = useState(target)
    const valueRef = useRef(target)
    const rafRef = useRef(0)

    useEffect(() => {
        const from = valueRef.current
        const to = Math.max(-1, Math.min(1, target))
        if (Math.abs(from - to) < 0.001) {
            valueRef.current = to
            setValue(to)
            return
        }

        const start = performance.now()
        cancelAnimationFrame(rafRef.current)

        const tick = (now: number) => {
            const t = Math.min(1, (now - start) / durationMs)
            const eased = 1 - (1 - t) ** 3
            const next = from + (to - from) * eased
            valueRef.current = next
            setValue(next)
            if (t < 1) {
                rafRef.current = requestAnimationFrame(tick)
            } else {
                valueRef.current = to
                setValue(to)
            }
        }

        rafRef.current = requestAnimationFrame(tick)
        return () => cancelAnimationFrame(rafRef.current)
    }, [target, durationMs])

    return value
}

type TechnicalsSummaryCardProps = {
    title: string
    label: TechnicalRatingLabel
    score: number
    counts: TechnicalActionCounts
    emphasized?: boolean
    showTitle?: boolean
    showCounts?: boolean
}

/**
 * SVG geometry — pivot/baseline sits on CY near the bottom of the viewBox
 * so the semicircle rises upward from a shared horizontal base.
 */
const VIEW_W = 200
const VIEW_H = 104
const CX = 100
const CY = 100
const RADIUS = 78
const STROKE = 8
const NEEDLE_LEN = 56

const useStyles = makeStyles()((theme) => ({
    root: {
        minWidth: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch'
    },
    title: {
        fontSize: 11,
        fontWeight: 500,
        color: theme.palette.text.secondary,
        textAlign: 'center',
        marginBottom: theme.spacing(0.25),
        flexShrink: 0
    },
    titleEmphasized: {
        fontSize: 13,
        fontWeight: 600
    },
    /** Only absorbs the height difference vs Summary so baselines stay aligned. */
    spacer: {
        flex: '1 1 0',
        minHeight: 0,
        maxHeight: 40
    },
    gaugeBlock: {
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch'
    },
    gaugeWrap: {
        width: '100%',
        maxWidth: 180,
        margin: '0 auto'
    },
    gaugeWrapEmphasized: {
        maxWidth: 240
    },
    svg: {
        display: 'block',
        width: '100%',
        height: 'auto',
        overflow: 'visible'
    },
    label: {
        fontSize: 16,
        fontWeight: 500,
        textAlign: 'center',
        lineHeight: 1.2,
        marginTop: theme.spacing(1.25),
        marginBottom: theme.spacing(1.5),
        transition: `color ${GAUGE_ANIMATION_MS}ms ease-out`
    },
    labelEmphasized: {
        fontSize: 20
    },
    countsRow: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: theme.spacing(1)
    },
    countCol: {
        flex: 1,
        textAlign: 'center',
        minWidth: 0
    },
    countCaption: {
        fontSize: 11,
        fontWeight: 500,
        color: theme.palette.text.secondary,
        lineHeight: 1.2,
        marginBottom: theme.spacing(0.25)
    },
    countValue: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.primary,
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1.2,
        transition: `opacity ${GAUGE_ANIMATION_MS}ms ease-out`
    },
    countValueEmphasized: {
        fontSize: 15
    }
}))

const labelColor = (
    label: TechnicalRatingLabel,
    theme: {
        palette: {
            text: { secondary: string }
            success: { main: string }
            error: { main: string }
        }
    }
) => {
    if (isBullishLabel(label)) return theme.palette.success.main
    if (isBearishLabel(label)) return theme.palette.error.main
    return theme.palette.text.secondary
}

/** score −1…+1 → needle rotation (−90 left, 0 up, +90 right). */
const scoreToNeedleRotation = (score: number): number => {
    const clamped = Math.max(-1, Math.min(1, score))
    return clamped * 90
}

/** Angle on the arc where the needle points (180 left → 0 right). */
const scoreToArcAngle = (score: number): number => {
    const clamped = Math.max(-1, Math.min(1, score))
    return 90 - clamped * 90
}

const polar = (angleDeg: number, r: number = RADIUS) => {
    const rad = (angleDeg * Math.PI) / 180
    return {
        x: CX + r * Math.cos(rad),
        y: CY - r * Math.sin(rad)
    }
}

/** Arc along the upper semicircle from startDeg → endDeg (angles decrease left→right). */
const strokeArcPath = (startDeg: number, endDeg: number): string => {
    const start = polar(startDeg)
    const end = polar(endDeg)
    const delta = Math.abs(startDeg - endDeg)
    if (delta < 0.5) {
        // Degenerate — draw a tiny stub so stroke caps still render at the left tip
        const tip = polar(startDeg)
        return `M ${tip.x} ${tip.y} L ${tip.x + 0.01} ${tip.y}`
    }
    const largeArc = delta > 180 ? 1 : 0
    return `M ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 ${largeArc} 1 ${end.x} ${end.y}`
}

const TechnicalsSummaryCard = ({
    title,
    label,
    score,
    counts,
    emphasized = false,
    showTitle = true,
    showCounts = true
}: TechnicalsSummaryCardProps) => {
    const { classes, cx } = useStyles()
    const theme = useTheme()
    const gradientId = `technicals-gauge-${useId().replace(/:/g, '')}`
    const animatedScore = useAnimatedScore(score)
    const needleRotation = scoreToNeedleRotation(animatedScore)
    const fillEndDeg = scoreToArcAngle(animatedScore)

    const trackPath = useMemo(() => strokeArcPath(180, 0), [])
    const fillPath = useMemo(() => strokeArcPath(180, fillEndDeg), [fillEndDeg])

    const gradientStops = useMemo(
        () => [
            { offset: '0%', color: theme.palette.error.dark },
            { offset: '22%', color: theme.palette.error.main },
            { offset: '40%', color: theme.palette.error.light },
            { offset: '50%', color: theme.palette.grey[500] },
            { offset: '60%', color: theme.palette.success.light },
            { offset: '78%', color: theme.palette.success.main },
            { offset: '100%', color: theme.palette.success.dark }
        ],
        [theme]
    )

    return (
        <Box className={classes.root}>
            {showTitle && (
                <Typography className={cx(classes.title, emphasized && classes.titleEmphasized)}>{title}</Typography>
            )}

            <Box className={classes.spacer} />

            <Box className={classes.gaugeBlock}>
                <Box className={cx(classes.gaugeWrap, emphasized && classes.gaugeWrapEmphasized)}>
                    <svg className={classes.svg} viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} aria-hidden>
                        <defs>
                            <linearGradient
                                id={gradientId}
                                gradientUnits="userSpaceOnUse"
                                x1={CX - RADIUS}
                                y1={CY}
                                x2={CX + RADIUS}
                                y2={CY}
                            >
                                {gradientStops.map((stop) => (
                                    <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
                                ))}
                            </linearGradient>
                        </defs>
                        {/* Gray track — full semicircle, flat butt ends on the baseline */}
                        <path
                            d={trackPath}
                            fill="none"
                            stroke={theme.palette.grey[700]}
                            strokeWidth={STROKE}
                            strokeLinecap="butt"
                        />
                        {/* Colored fill from left (Strong Sell) up to the needle */}
                        <path
                            d={fillPath}
                            fill="none"
                            stroke={`url(#${gradientId})`}
                            strokeWidth={STROKE}
                            strokeLinecap="butt"
                        />
                        <g transform={`rotate(${needleRotation} ${CX} ${CY})`}>
                            <line
                                x1={CX}
                                y1={CY}
                                x2={CX}
                                y2={CY - NEEDLE_LEN}
                                stroke={theme.palette.text.primary}
                                strokeWidth={2.25}
                                strokeLinecap="round"
                            />
                            <circle
                                cx={CX}
                                cy={CY}
                                r={4}
                                fill={theme.palette.background.paper}
                                stroke={theme.palette.text.primary}
                                strokeWidth={2}
                            />
                        </g>
                    </svg>
                </Box>

                <Typography
                    className={cx(classes.label, emphasized && classes.labelEmphasized)}
                    sx={{ color: labelColor(label, theme) }}
                >
                    {label}
                </Typography>

                {showCounts && (
                    <Box className={classes.countsRow}>
                        <Box className={classes.countCol}>
                            <Typography className={classes.countCaption}>Sell</Typography>
                            <Typography className={cx(classes.countValue, emphasized && classes.countValueEmphasized)}>
                                {counts.sell}
                            </Typography>
                        </Box>
                        <Box className={classes.countCol}>
                            <Typography className={classes.countCaption}>Neutral</Typography>
                            <Typography className={cx(classes.countValue, emphasized && classes.countValueEmphasized)}>
                                {counts.neutral}
                            </Typography>
                        </Box>
                        <Box className={classes.countCol}>
                            <Typography className={classes.countCaption}>Buy</Typography>
                            <Typography className={cx(classes.countValue, emphasized && classes.countValueEmphasized)}>
                                {counts.buy}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default memo(TechnicalsSummaryCard)
